import React, { createContext, ReactNode, useEffect, useRef, useState } from 'react'
import { WEB_SOCKET_URL } from '../../assets/constants'
import { WebSocketResponseMessage, WebSocketResponseMessage_CandleSubscriptionResponse, WebSocketResponseMessage_OrderUpdate } from '../../proto/generated/websocket'
import { useSelector } from 'react-redux'
import { selectIsEmbedded } from '../../selectors/device'
import { isEmbeddedStrategyChartContext } from '../../util/embeddedChartContext'

export interface WebSocketContextValue {
    socket: WebSocket | null
    sendMessage: (message: Uint8Array) => void
    addListener: (listener: (data: Uint8Array) => void) => void
    removeListener: (listener: (data: Uint8Array) => void) => void
}

interface WebSocketProviderProps {
    children: ReactNode
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

export { WebSocketContext }

const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
    const socketRef = useRef<WebSocket | null>(null)
    const [socket, setSocket] = useState<WebSocket | null>(null)
    const listeners = useRef<((data: Uint8Array) => void)[]>([]);
    const isEmbeded = useSelector(selectIsEmbedded);

    const sendMessage = (message: Uint8Array) => {
        if (isEmbeded) return;
        const currentSocket = init()

        currentSocket.onmessage = (msg) => {
            const data = new Uint8Array(msg.data)
            if (listeners.current.length) {
                listeners.current.forEach(callback => callback(data))
            }
        }

        if (currentSocket.readyState === WebSocket.OPEN) {
            currentSocket.send(message)
        } else {
            currentSocket.onopen = function () {
                currentSocket.readyState === WebSocket.OPEN && currentSocket.send(message)
            }
        }
    }

    useEffect(() => {
        if (!isEmbeded) return
        const chartBridge = isEmbeddedStrategyChartContext()
            ? window.strategyChartManager
            : window.chartManager
        if (!chartBridge) return
        chartBridge._publishToWebsocketManager = (candle: WebSocketResponseMessage_CandleSubscriptionResponse) => {
            const socketMessage = WebSocketResponseMessage.create();
            socketMessage.candleSubscriptionResponses = [candle];
            const m = WebSocketResponseMessage.encode(socketMessage).finish()

            if (listeners.current.length) {
                listeners.current.forEach(callback => callback( m ))
            }
        }
        if (isEmbeded && window.tradeManager) {
            window.tradeManager._publishOrderUpdatesToWebsocketManager = (orderUpdate: WebSocketResponseMessage_OrderUpdate) => {
                const socketMessage = WebSocketResponseMessage.create();
                socketMessage.orderUpdate = orderUpdate;
                const m = WebSocketResponseMessage.encode(socketMessage).finish()

                if (listeners.current.length) {
                    listeners.current.forEach(callback => callback( m ))
                }
            }
        }
    }, [isEmbeded, listeners])

    const init = (): WebSocket => {
        if (!socketRef.current) {
            const newSocket = new WebSocket(WEB_SOCKET_URL)
            newSocket.binaryType = 'arraybuffer'
            // (window as any).getWebSocket = (newSocket.readyState !== WebSocket.OPEN) ? new WebSocket(WEB_SOCKET_URL) : newSocket
            ;(window as any).getWebSocket = newSocket

            newSocket.onopen = function () {}

            newSocket.onclose = (ev) => {
                console.log('socket closed', ev, JSON.stringify(ev))
                socketRef.current = null
                setSocket(null)
                reconnect()
            }

            newSocket.onerror = (ev) => {
                newSocket.close()
            }
            socketRef.current = newSocket
            setSocket(newSocket)
        }

        return socketRef.current as WebSocket
    }

    function reconnect() {
        setTimeout(init, 2000)
    }

    React.useEffect(() => {
        !isEmbeded && init();

        // unmount
        return () => {
            if (socketRef.current) {
                listeners.current = []
                socketRef.current.close()
            }
        }
    }, [])

    const contextValue: WebSocketContextValue = {
        socket: socket,
        sendMessage: sendMessage,
        addListener: (listener: (data: Uint8Array) => void) => {
            listeners.current.push(listener)
        },
        removeListener: (listener: (data: Uint8Array) => void) => {
            listeners.current = listeners.current.filter((ls) => ls !== listener)
        }
    }

    return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>
}

export default WebSocketProvider
